import { DefaultFormMultiSelectField } from "../../../../common/components/forms/inputs/dropdown/DefaultFormMultiSelectField";
import { IEventPickerOption } from "../utils/eventPickerOptionUtils";
import styles from "./EventPickerMultiSelectField.module.scss";

interface IEventPickerMultiSelectFieldProps {
  name: string;
  label: string;
  options: IEventPickerOption[];
  emptyMessage: string;
  disabled?: boolean;
  /**
   * Past this many selections the field shows `selectedItemsLabel` instead of one chip per
   * event — a picker that can select a year of classes must not grow a chip for each.
   */
  maxSelectedLabels?: number;
  selectedItemsLabel?: string;
}

/**
 * The date on its own line, then the name and current instructor wrapping over up to two more.
 * Wrapping rather than clipping keeps the panel from scrolling sideways, and the row's fixed
 * height is what a virtualised list needs.
 */
const eventItemTemplate = (option: IEventPickerOption) => (
  <div className={styles.root_option}>
    <span className={styles.root_option_primary}>{option.primary}</span>
    <span className={styles.root_option_secondary}>{option.secondary}</span>
  </div>
);

/**
 * The item's inline border-box height: `.root_option` (54px) plus the item's own 8px vertical
 * padding. Its margin is zeroed in the stylesheet, so a row advances exactly this much — which
 * is what the virtual scroller assumes when it positions the window.
 */
const EVENT_ROW_HEIGHT = 70;

/**
 * Virtualised multi-select of planner events, shared by the instructor transfer and the bulk
 * delete dialogs: some studios have thousands of future classes.
 */
export const EventPickerMultiSelectField = ({
  name,
  label,
  options,
  emptyMessage,
  disabled,
  maxSelectedLabels,
  selectedItemsLabel,
}: IEventPickerMultiSelectFieldProps) => (
  <DefaultFormMultiSelectField<IEventPickerOption>
    name={name}
    label={label}
    required
    options={options}
    disabled={disabled}
    includeId={false}
    display="chip"
    emptyMessage={emptyMessage}
    filter
    width="100%"
    // PrimeReact's virtual scroller renders nothing at all for an empty list - its
    // content template only covers the "filter matched nothing" case - so the panel
    // collapses to zero height and looks like it never opened. The plain list does
    // render emptyMessage, so drop virtualisation exactly when there is nothing to
    // virtualise.
    virtualScrollerOptions={
      options.length > 0 ? { itemSize: EVENT_ROW_HEIGHT } : undefined
    }
    itemTemplate={eventItemTemplate}
    panelClassName={styles.root_panel}
    maxSelectedLabels={maxSelectedLabels}
    selectedItemsLabel={selectedItemsLabel}
  />
);
